import {
  MetricsCard,
  PageHeader,
  type SelectOption,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@getdevpulse/shared-ui"
import { observer } from "mobx-react-lite"
import { useMemo, useState } from "react"
import {
  calculateMetrics,
  calculatePerformanceScore,
  type PerformanceScore,
} from "../lib/performanceScore"
import { useDataStore, useI18nStore } from "../stores/StoreContext"

type TimeRange = "7days" | "30days" | "all"

interface ContributorStats {
  authorHandle: string
  score: number
  prCount: number
  scoreHistory: Array<{ label: string; value: number }>
  prHistory: Array<{ label: string; value: number }>
  performanceScore: PerformanceScore
  performanceHistory: Array<{ label: string; value: number }>
}

// Helper to get date range start based on time range
const getDateRangeStart = (timeRange: TimeRange): Date => {
  const now = new Date()
  if (timeRange === "7days") {
    return new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
  }
  if (timeRange === "30days") {
    return new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000)
  }
  return new Date(0) // All time
}

// Helper to determine chart granularity and period based on available data
const getChartPeriod = (
  timeRange: TimeRange,
  oldestDate: Date,
): { periods: number; unit: "day" | "week" | "month" } => {
  const now = new Date()
  const totalDays = Math.floor((now.getTime() - oldestDate.getTime()) / (24 * 60 * 60 * 1000))
  const totalWeeks = Math.floor(totalDays / 7)
  const totalMonths = Math.floor(totalDays / 30)

  if (timeRange === "7days") {
    return { periods: 7, unit: "day" }
  }

  if (timeRange === "30days") {
    // If >= 4 weeks available: last 4 weeks, else last 7 days
    if (totalWeeks >= 4) {
      return { periods: 4, unit: "week" }
    }
    return { periods: 7, unit: "day" }
  }

  // timeRange === "all"
  // If >= 3 months: last 6 months, else if >= 4 weeks: last 4 weeks, else last 7 days
  if (totalMonths >= 3) {
    return { periods: 6, unit: "month" }
  }
  if (totalWeeks >= 4) {
    return { periods: 4, unit: "week" }
  }
  return { periods: 7, unit: "day" }
}

// Helper to format date labels
const formatDateLabel = (date: Date, unit: "day" | "week" | "month"): string => {
  if (unit === "month") {
    return date.toLocaleDateString("en-US", {
      month: "short",
      year: "numeric",
    })
  }
  if (unit === "week") {
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" })
  }
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" })
}

// Helper to get period start date
const getPeriodStart = (
  periodIndex: number,
  totalPeriods: number,
  unit: "day" | "week" | "month",
): Date => {
  const now = new Date()
  const periodOffset = totalPeriods - periodIndex - 1

  if (unit === "day") {
    return new Date(now.getTime() - periodOffset * 24 * 60 * 60 * 1000)
  }
  if (unit === "week") {
    return new Date(now.getTime() - periodOffset * 7 * 24 * 60 * 60 * 1000)
  }
  // month
  const date = new Date(now)
  date.setMonth(date.getMonth() - periodOffset)
  return date
}

export const ContributorsPage = observer(() => {
  const dataStore = useDataStore()
  const i18nStore = useI18nStore()
  const [selectedTimeRange, setSelectedTimeRange] = useState<TimeRange>("30days")

  // Time range options for the dropdown
  const timeRangeOptions: SelectOption[] = [
    { id: "7days", label: i18nStore.t("timeLast7Days") },
    { id: "30days", label: i18nStore.t("timeLast30Days") },
    { id: "all", label: i18nStore.t("timeAll") },
  ]

  // Calculate date range based on selection
  const dateRange = useMemo(() => {
    return getDateRangeStart(selectedTimeRange)
  }, [selectedTimeRange])

  // Get the timestamp for dependency tracking (Date objects don't work well in deps)
  const dateRangeTimestamp = dateRange.getTime()

  // Calculate the time range in days for performance score normalization
  const timeRangeDays = useMemo(() => {
    if (selectedTimeRange === "7days") return 7
    if (selectedTimeRange === "30days") return 30
    // For "all", calculate actual days from oldest data to now
    const now = new Date()
    const oldestDate = new Date(
      Math.min(
        ...dataStore.allCommits.map((c) => c.timestampDate.getTime()),
        ...dataStore.allPullRequests.map((pr) => pr.createdAtDate?.getTime() || Infinity),
      ),
    )
    return Math.max(30, Math.floor((now.getTime() - oldestDate.getTime()) / (24 * 60 * 60 * 1000)))
  }, [selectedTimeRange, dataStore.allCommits, dataStore.allPullRequests])

  // Get oldest data date for chart period calculation
  const oldestDataDate = useMemo(() => {
    let oldest = new Date()

    dataStore.allCommits.forEach((commit) => {
      if (commit.timestampDate < oldest) {
        oldest = commit.timestampDate
      }
    })

    dataStore.allPullRequests.forEach((pr) => {
      if (pr.createdAtDate && pr.createdAtDate < oldest) {
        oldest = pr.createdAtDate
      }
    })

    return oldest
  }, [dataStore.allCommits, dataStore.allPullRequests])

  // Determine chart period configuration
  const chartConfig = useMemo(() => {
    return getChartPeriod(selectedTimeRange, oldestDataDate)
  }, [selectedTimeRange, oldestDataDate])

  // Calculate contributor statistics with historical data
  // biome-ignore lint/correctness/useExhaustiveDependencies: MobX observable arrays require snapshot conversion via .slice()
  const contributorStats = useMemo(() => {
    // Create a snapshot of the data to avoid MobX reactivity issues
    const commits = dataStore.allCommits.slice()
    const pullRequests = dataStore.allPullRequests.slice()
    const issues = dataStore.allIssues.slice()

    const statsMap = new Map<string, ContributorStats>()
    const { periods, unit } = chartConfig

    // Initialize periods for all contributors
    const initializeContributor = (handle: string): ContributorStats => {
      const history: Array<{ label: string; value: number }> = []

      for (let i = 0; i < periods; i++) {
        const periodStart = getPeriodStart(i, periods, unit)
        history.push({
          label: formatDateLabel(periodStart, unit),
          value: 0,
        })
      }

      // Initialize with empty performance score
      const emptyMetrics = calculateMetrics([], [], [])
      const emptyScore = calculatePerformanceScore(emptyMetrics, timeRangeDays)

      return {
        authorHandle: handle,
        score: 0,
        prCount: 0,
        scoreHistory: history.map((h) => ({ ...h })),
        prHistory: history.map((h) => ({ ...h })),
        performanceScore: emptyScore,
        performanceHistory: history.map((h) => ({ ...h })),
      }
    }

    // Helper to get period index for a date
    const getPeriodIndex = (date: Date): number => {
      const now = new Date()
      let diff: number

      if (unit === "day") {
        diff = Math.floor((now.getTime() - date.getTime()) / (24 * 60 * 60 * 1000))
      } else if (unit === "week") {
        diff = Math.floor((now.getTime() - date.getTime()) / (7 * 24 * 60 * 60 * 1000))
      } else {
        // month
        const monthsDiff =
          (now.getFullYear() - date.getFullYear()) * 12 + (now.getMonth() - date.getMonth())
        diff = monthsDiff
      }

      const periodIndex = periods - diff - 1
      return periodIndex >= 0 && periodIndex < periods ? periodIndex : -1
    }

    // Track data per contributor per period for performance score calculation
    const contributorDataByPeriod = new Map<
      string,
      Map<
        number,
        {
          commits: Array<{
            score: number
            insertions: number
            deletions: number
            filesChanged: number
            commitType?: string
          }>
          prs: Array<{ additions: number; deletions: number; state: string }>
          issues: Array<{ state: string }>
        }
      >
    >()

    // Initialize data structure
    const getContributorPeriodData = (handle: string, periodIndex: number) => {
      if (!contributorDataByPeriod.has(handle)) {
        contributorDataByPeriod.set(handle, new Map())
      }
      const periodMap = contributorDataByPeriod.get(handle)
      if (!periodMap) {
        throw new Error("Period map should exist")
      }
      if (!periodMap.has(periodIndex)) {
        periodMap.set(periodIndex, { commits: [], prs: [], issues: [] })
      }
      const periodData = periodMap.get(periodIndex)
      if (!periodData) {
        throw new Error("Period data should exist")
      }
      return periodData
    }

    // Process commits for scores and track by period
    commits.forEach((commit) => {
      if (commit.timestampDate >= dateRange) {
        const current =
          statsMap.get(commit.authorHandle) || initializeContributor(commit.authorHandle)
        current.score += commit.score || 0

        // Add to historical data
        const periodIndex = getPeriodIndex(commit.timestampDate)
        if (periodIndex >= 0) {
          current.scoreHistory[periodIndex].value += commit.score || 0

          // Track commit data for performance score calculation
          const periodData = getContributorPeriodData(commit.authorHandle, periodIndex)
          periodData.commits.push({
            score: commit.score || 0,
            insertions: commit.insertions || 0,
            deletions: commit.deletions || 0,
            filesChanged: commit.filesChanged || 0,
            commitType: commit.commitType,
          })
        }

        statsMap.set(commit.authorHandle, current)
      }
    })

    // Process PRs for counts (merged PRs)
    pullRequests.forEach((pr) => {
      if (pr.state === "merged" && pr.mergedAtDate && pr.mergedAtDate >= dateRange) {
        const current = statsMap.get(pr.creatorHandle) || initializeContributor(pr.creatorHandle)
        current.prCount += 1

        // Add to historical data
        const periodIndex = getPeriodIndex(pr.mergedAtDate)
        if (periodIndex >= 0) {
          current.prHistory[periodIndex].value += 1

          // Track PR data for performance score calculation
          const periodData = getContributorPeriodData(pr.creatorHandle, periodIndex)
          periodData.prs.push({
            additions: pr.additions || 0,
            deletions: pr.deletions || 0,
            state: pr.state,
          })
        }

        statsMap.set(pr.creatorHandle, current)
      }
    })

    // Process issues (all within date range)
    issues.forEach((issue) => {
      if (issue.closedAtDate && issue.closedAtDate >= dateRange) {
        const handle = issue.closedByHandle || issue.creatorHandle
        const current = statsMap.get(handle) || initializeContributor(handle)

        // Add to historical data
        const periodIndex = getPeriodIndex(issue.closedAtDate)
        if (periodIndex >= 0) {
          // Track issue data for performance score calculation
          const periodData = getContributorPeriodData(handle, periodIndex)
          periodData.issues.push({
            state: issue.state,
          })
        }

        statsMap.set(handle, current)
      }
    })

    // Calculate performance scores for each contributor
    for (const [handle, contributor] of statsMap.entries()) {
      const periodMap = contributorDataByPeriod.get(handle)

      // Calculate overall performance score
      const allCommits: Array<{
        score: number
        insertions: number
        deletions: number
        filesChanged: number
        commitType?: string
      }> = []
      const allPRs: Array<{
        additions: number
        deletions: number
        state: string
      }> = []
      const allIssues: Array<{
        state: string
      }> = []

      if (periodMap) {
        for (const periodData of periodMap.values()) {
          allCommits.push(...periodData.commits)
          allPRs.push(...periodData.prs)
          allIssues.push(...periodData.issues)
        }
      }

      const metrics = calculateMetrics(allCommits, allPRs, allIssues)
      contributor.performanceScore = calculatePerformanceScore(metrics, timeRangeDays)

      // Calculate performance score history per period
      for (let i = 0; i < periods; i++) {
        const periodData = periodMap?.get(i)
        if (periodData) {
          const periodMetrics = calculateMetrics(
            periodData.commits,
            periodData.prs,
            periodData.issues,
          )
          // For historical periods, use the unit-based duration
          const periodDays = unit === "day" ? 1 : unit === "week" ? 7 : 30
          const periodScore = calculatePerformanceScore(periodMetrics, periodDays)
          contributor.performanceHistory[i].value = periodScore.overall
        }
      }
    }

    return Array.from(statsMap.values())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dateRangeTimestamp,
    timeRangeDays,
    chartConfig.periods,
    chartConfig.unit,
    // Force recalculation when data changes
    dataStore.allCommits.length,
    dataStore.allPullRequests.length,
    dataStore.allIssues.length,
  ])

  // Calculate score statistics
  const scoreStats = useMemo(() => {
    const sorted = [...contributorStats].sort((a, b) => b.score - a.score)
    const totalScore = sorted.reduce((sum, c) => sum + c.score, 0)
    const average = sorted.length > 0 ? totalScore / sorted.length : 0

    return {
      sorted,
      average,
      total: totalScore,
    }
  }, [contributorStats])

  // Calculate PR statistics
  const prStats = useMemo(() => {
    const sorted = [...contributorStats].sort((a, b) => b.prCount - a.prCount)
    const totalPRs = sorted.reduce((sum, c) => sum + c.prCount, 0)
    const average = sorted.length > 0 ? totalPRs / sorted.length : 0

    return {
      sorted,
      average,
      total: totalPRs,
    }
  }, [contributorStats])

  // Calculate performance statistics
  const performanceStats = useMemo(() => {
    const sorted = [...contributorStats].sort(
      (a, b) => b.performanceScore.overall - a.performanceScore.overall,
    )
    const totalPerformance = sorted.reduce((sum, c) => sum + c.performanceScore.overall, 0)
    const average = sorted.length > 0 ? totalPerformance / sorted.length : 0

    return {
      sorted,
      average,
      total: totalPerformance,
    }
  }, [contributorStats])

  return (
    <div className="min-h-full bg-background">
      <div className="container mx-auto p-3 sm:p-4 md:p-6">
        <PageHeader
          title={i18nStore.t("contributorsTitle")}
          subtitle={i18nStore.t("contributorsSubtitle")}
          selectOptions={timeRangeOptions}
          selectedOptionId={selectedTimeRange}
          onSelectChange={(id) => setSelectedTimeRange(id as TimeRange)}
          selectPlaceholder={i18nStore.t("selectTimeRange")}
        >
          <Tabs defaultValue="performance" className="w-full">
            <TabsList className="grid w-full max-w-2xl grid-cols-3">
              <TabsTrigger value="performance">{i18nStore.t("tabPerformance")}</TabsTrigger>
              <TabsTrigger value="score">{i18nStore.t("tabScore")}</TabsTrigger>
              <TabsTrigger value="prs">{i18nStore.t("tabPRs")}</TabsTrigger>
            </TabsList>

            {/* Performance Tab */}
            <TabsContent value="performance" className="space-y-6 mt-6">
              <div className="mb-6 p-4 rounded-lg bg-muted/50 border border-border">
                <h3 className="text-sm font-semibold mb-2">
                  {i18nStore.t("performanceScoreTitle")}
                </h3>
                <p className="text-sm text-muted-foreground">
                  {i18nStore.t("performanceScoreDescription")}
                  <span className="font-medium text-foreground">
                    {" "}
                    {i18nStore.tf("performanceScoreAverage", {
                      value: performanceStats.average.toFixed(1),
                    })}
                  </span>{" "}
                  {i18nStore.t("performanceScoreComparison")}
                </p>
              </div>

              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {performanceStats.sorted.map((contributor) => {
                  const percentDiff =
                    performanceStats.average > 0
                      ? ((contributor.performanceScore.overall - performanceStats.average) /
                          performanceStats.average) *
                        100
                      : 0
                  const trend: "up" | "down" | "neutral" =
                    percentDiff > 0 ? "up" : percentDiff < 0 ? "down" : "neutral"

                  return (
                    <a
                      key={contributor.authorHandle}
                      href={`https://github.com/${contributor.authorHandle}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block no-underline"
                    >
                      <MetricsCard
                        title={contributor.authorHandle}
                        description={contributor.performanceScore.overall.toFixed(1)}
                        percentage={Number(percentDiff.toFixed(1))}
                        trend={trend}
                        chartData={contributor.performanceHistory}
                        chartType="line"
                      />
                    </a>
                  )
                })}
              </div>

              {performanceStats.sorted.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  {i18nStore.t("contributorsNoData")}
                </div>
              )}
            </TabsContent>

            {/* Score Tab */}
            <TabsContent value="score" className="space-y-6 mt-6">
              <div className="mb-6 p-4 rounded-lg bg-muted/50 border border-border">
                <h3 className="text-sm font-semibold mb-2">{i18nStore.t("scoreTitle")}</h3>
                <p className="text-sm text-muted-foreground">
                  {i18nStore.t("scoreDescription")}
                  <span className="font-medium text-foreground">
                    {" "}
                    {i18nStore.tf("scoreAverage", {
                      value: scoreStats.average.toFixed(0),
                    })}
                  </span>{" "}
                  {i18nStore.t("scoreComparison")}
                </p>
              </div>

              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {scoreStats.sorted.map((contributor) => {
                  const percentDiff =
                    scoreStats.average > 0
                      ? ((contributor.score - scoreStats.average) / scoreStats.average) * 100
                      : 0
                  const trend: "up" | "down" | "neutral" =
                    percentDiff > 0 ? "up" : percentDiff < 0 ? "down" : "neutral"

                  return (
                    <a
                      key={contributor.authorHandle}
                      href={`https://github.com/${contributor.authorHandle}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block no-underline"
                    >
                      <MetricsCard
                        title={contributor.authorHandle}
                        description={contributor.score.toLocaleString()}
                        percentage={Number(percentDiff.toFixed(1))}
                        trend={trend}
                        chartData={contributor.scoreHistory}
                        chartType="line"
                      />
                    </a>
                  )
                })}
              </div>

              {scoreStats.sorted.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  {i18nStore.t("contributorsNoData")}
                </div>
              )}
            </TabsContent>

            {/* PRs Tab */}
            <TabsContent value="prs" className="space-y-6 mt-6">
              <div className="mb-6 p-4 rounded-lg bg-muted/50 border border-border">
                <h3 className="text-sm font-semibold mb-2">{i18nStore.t("prsTitle")}</h3>
                <p className="text-sm text-muted-foreground">
                  {i18nStore.t("prsDescription")}
                  <span className="font-medium text-foreground">
                    {" "}
                    {i18nStore.tf("prsAverage", {
                      value: prStats.average.toFixed(1),
                    })}
                  </span>{" "}
                  {i18nStore.t("prsComparison")}
                </p>
              </div>

              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                {prStats.sorted.map((contributor) => {
                  const percentDiff =
                    prStats.average > 0
                      ? ((contributor.prCount - prStats.average) / prStats.average) * 100
                      : 0
                  const trend: "up" | "down" | "neutral" =
                    percentDiff > 0 ? "up" : percentDiff < 0 ? "down" : "neutral"

                  return (
                    <a
                      key={contributor.authorHandle}
                      href={`https://github.com/${contributor.authorHandle}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block no-underline"
                    >
                      <MetricsCard
                        title={contributor.authorHandle}
                        description={contributor.prCount.toString()}
                        percentage={Number(percentDiff.toFixed(1))}
                        trend={trend}
                        chartData={contributor.prHistory}
                        chartType="line"
                      />
                    </a>
                  )
                })}
              </div>

              {prStats.sorted.length === 0 && (
                <div className="text-center py-12 text-muted-foreground">
                  {i18nStore.t("contributorsNoData")}
                </div>
              )}
            </TabsContent>
          </Tabs>
        </PageHeader>
      </div>
    </div>
  )
})

ContributorsPage.displayName = "ContributorsPage"
